import React, { ReactElement, CSSProperties, useMemo } from 'react';
import { List } from 'react-window';

const ITEM_HEIGHT = 48;

interface SimpleVirtualizedListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactElement;
  height?: number;
  maxHeight?: number;
  itemHeight?: number;
  overscanCount?: number;
}

interface ItemData<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactElement;
}

interface RowComponentProps<T> extends ItemData<T> {
  ariaAttributes: {
    'aria-posinset': number;
    'aria-setsize': number;
    role: 'listitem';
  };
  index: number;
  style: CSSProperties;
}

const Row = <T,>({ index, style, items, renderItem }: RowComponentProps<T>) => {
  return <div style={style}>{renderItem(items[index], index)}</div>;
};

/**
 * A general-purpose virtualized list component for rendering large lists efficiently.
 *
 * Use this when:
 * - You have all data loaded at once (non-paginated)
 * - You want to render a large list without performance issues
 * - You need a simple, reusable virtualized list
 *
 * For paginated/infinite scroll lists with autocomplete, use VirtualizedAutocomplete instead.
 */
export function VirtualizedList<T>({
  items,
  renderItem,
  height,
  maxHeight,
  itemHeight = ITEM_HEIGHT,
  overscanCount = 5,
}: SimpleVirtualizedListProps<T>) {
  // Memoize the item data to prevent unnecessary re-renders
  const itemData = useMemo<ItemData<T>>(
    () => ({ items, renderItem }),
    [items, renderItem],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <List
      rowHeight={itemHeight}
      rowCount={items.length}
      rowComponent={Row}
      rowProps={itemData}
      overscanCount={overscanCount}
      style={{ height: height, maxHeight: maxHeight, width: '100%' }}
    />
  );
}
